// Admin → Connectors — where the security team gives CyberWISE the keys it
// reads each security tool with. Chauncey 2026-10-05: "we should be building
// that into the admin portal for it. Not hardcoding it as usual."
//
// What a person can and cannot do here is the point of the screen:
//   · ENTERED connectors (Falcon, Abnormal): type the credential once. The
//     secret field is write-only — after saving, the screen shows that a
//     secret is set, its last four characters, who set it and when. There is
//     no reveal; a key that needs re-reading is a key to rotate.
//   · MANAGED-IDENTITY connectors (Entra, Defender): nothing to type. The
//     screen shows each Microsoft API permission the identity needs as
//     GRANTED, MISSING, or CAN'T TELL — three answers, because "can't tell"
//     (e.g. running locally as a person) is not the same as "missing".
//   · Every save runs a test immediately, and the result is shown in the
//     vendor's own words. Every change and every test is in the audit log.

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { AdminConnector, AuditEntry, GrantState } from '../../lib/types';
import { Pill } from '../primitives';
import { PageHeader } from '../Shell';
import { StateMessage } from '../StateMessage';

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '—';

const ACTION_LABEL: Record<string, string> = {
  'secret.set': 'Credential entered',
  'secret.cleared': 'Credential cleared',
  'secret.retire_failed': 'Old credential NOT retired',
  'settings.updated': 'Settings changed',
  'test.passed': 'Test passed',
  'test.failed': 'Test failed',
};

const GRANT: Record<GrantState, { kind: 'ok' | 'bad' | 'neutral'; label: string }> = {
  granted: { kind: 'ok', label: 'Granted' },
  missing: { kind: 'bad', label: 'Missing' },
  unknown: { kind: 'neutral', label: "Can't tell" },
};

function statusOf(c: AdminConnector): { kind: 'ok' | 'warn' | 'bad' | 'neutral'; label: string } {
  if (c.auth === 'entered' && !c.secret?.set) return { kind: 'neutral', label: 'Not connected' };
  if (c.lastTest?.ok === true) return { kind: 'ok', label: 'Test passed' };
  if (c.lastTest?.ok === false) return { kind: 'bad', label: 'Test failed' };
  if (c.auth === 'managed_identity') {
    const states = c.roles?.checks.map((r) => r.state) ?? [];
    if (states.includes('missing')) return { kind: 'warn', label: 'Permissions missing' };
  }
  return { kind: 'neutral', label: 'Not tested yet' };
}

function EnteredForm({ c, onDone }: { c: AdminConnector; onDone: () => void }) {
  const initial = Object.fromEntries(c.fields.map((f) => [f.key, f.secret ? '' : (f.value ?? '')]));
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [busy, setBusy] = useState<'save' | 'test' | 'clear' | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const hasSecret = Boolean(c.secret?.set);

  const run = async (kind: 'save' | 'test' | 'clear') => {
    setBusy(kind);
    setResult(null);
    try {
      if (kind === 'save') {
        const r = await api.admin.save(c.id, values);
        setResult({ ok: r.test.ok, text: r.test.ok ? `Saved and tested. ${r.test.detail}` : `Saved, but the test failed: ${r.test.detail}` });
        // The secret field never keeps what was typed once it has been sent.
        setValues((v) => ({ ...v, ...Object.fromEntries(c.fields.filter((f) => f.secret).map((f) => [f.key, ''])) }));
      } else if (kind === 'test') {
        const r = await api.admin.test(c.id);
        setResult({ ok: r.ok, text: r.detail });
      } else {
        await api.admin.clear(c.id);
        setResult({ ok: true, text: `Cleared. ${c.name} is no longer connected and shows nothing until a new credential is entered.` });
      }
      onDone();
    } catch (err) {
      setResult({ ok: false, text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <form
      className="cw-form"
      onSubmit={(e) => {
        e.preventDefault();
        void run('save');
      }}
    >
      {c.storeUnavailable && <div className="note bad">{c.storeUnavailable}</div>}
      {c.fields.map((f) => (
        <label key={f.key} className="cw-field">
          <span className="cw-field-l">
            {f.label}
            {f.required && !(f.secret && hasSecret) ? <span className="req"> *</span> : null}
          </span>
          {f.options ? (
            <select
              className="search"
              value={values[f.key] ?? ''}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              required={f.required}
            >
              <option value="">Choose…</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="search"
              type={f.secret ? 'password' : 'text'}
              autoComplete={f.secret ? 'new-password' : 'off'}
              spellCheck={false}
              value={values[f.key] ?? ''}
              placeholder={
                f.secret && hasSecret
                  ? `Set${c.secret?.hint ? ` — ends …${c.secret.hint}` : ''}. Leave blank to keep it; type a new one to replace it.`
                  : ''
              }
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              required={f.required && !(f.secret && hasSecret)}
            />
          )}
          {f.help && <span className="cw-field-h">{f.help}</span>}
        </label>
      ))}

      {hasSecret && c.secret && (
        <div className="cw-set">
          Credential set by <b>{c.secret.setBy ?? 'unknown'}</b> on {when(c.secret.setAt)}
          {c.secret.hint ? (
            <>
              {' '}
              · ends <span className="mono">…{c.secret.hint}</span>
            </>
          ) : null}
        </div>
      )}

      <div className="cw-form-acts">
        <button className="btn btn-p" type="submit" disabled={busy !== null || Boolean(c.storeUnavailable)}>
          {busy === 'save' ? 'Saving and testing…' : hasSecret ? 'Save changes' : 'Save and test'}
        </button>
        {hasSecret && (
          <button className="btn btn-o" type="button" disabled={busy !== null} onClick={() => void run('test')}>
            {busy === 'test' ? 'Testing…' : 'Test again'}
          </button>
        )}
        {hasSecret && (
          <button
            className="btn btn-o cw-danger"
            type="button"
            disabled={busy !== null}
            onClick={() => {
              if (window.confirm(`Clear the ${c.name} credential? CyberWISE stops reading ${c.vendor} until a new one is entered.`)) void run('clear');
            }}
          >
            {busy === 'clear' ? 'Clearing…' : 'Clear credential'}
          </button>
        )}
      </div>
      {result && <div className={`note${result.ok ? '' : ' bad'}`}>{result.text}</div>}
    </form>
  );
}

function ManagedIdentity({ c, onDone }: { c: AdminConnector; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const checks = c.roles?.checks ?? [];
  const byResource = [...new Set(checks.map((r) => r.resourceLabel))];

  return (
    <div className="cw-form">
      {c.roles && <div className="cw-set">Signs in as {c.roles.identity}.</div>}
      {c.roles?.error && <div className="note bad">CyberWISE could not get a token to check: {c.roles.error}</div>}
      {byResource.map((res) => (
        <div key={res}>
          <div className="cw-field-l">{res}</div>
          <table className="t cw-roles">
            <tbody>
              {checks
                .filter((r) => r.resourceLabel === res)
                .map((r) => (
                  <tr key={r.role}>
                    <td className="mono">{r.role}</td>
                    <td>{r.why}</td>
                    <td className="num">
                      <Pill kind={GRANT[r.state].kind}>{GRANT[r.state].label}</Pill>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ))}
      {checks.some((r) => r.state === 'unknown') && !c.roles?.error && (
        <div className="cw-field-h">
          &ldquo;Can&rsquo;t tell&rdquo; means CyberWISE is signed in as a person here, not as its managed identity, and a person&rsquo;s token does not list app
          permissions. It is not the same as missing.
        </div>
      )}
      <div className="cw-form-acts">
        <button
          className="btn btn-o"
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setResult(null);
            api.admin
              .test(c.id)
              .then((r) => setResult({ ok: r.ok, text: r.detail }))
              .catch((err: unknown) => setResult({ ok: false, text: err instanceof ApiError ? err.message : String(err) }))
              .finally(() => {
                setBusy(false);
                onDone();
              });
          }}
        >
          {busy ? 'Testing…' : 'Test connection'}
        </button>
      </div>
      {result && <div className={`note${result.ok ? '' : ' bad'}`}>{result.text}</div>}
    </div>
  );
}

export function AdminConnectors() {
  const [connectors, setConnectors] = useState<AdminConnector[] | null>(null);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(() => {
    setError(null);
    Promise.all([api.admin.connectors(), api.admin.audit(50)])
      .then(([c, a]) => {
        setConnectors(c.connectors);
        setAudit(a.entries);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err : new ApiError(0, String(err))));
  }, []);

  useEffect(load, [load]);

  const header = (
    <PageHeader
      title="Connectors"
      subtitle="The credentials CyberWISE reads each security tool with. Secrets are kept in Key Vault and are never shown again — not here, not to anyone."
    />
  );

  if (error) {
    return (
      <>
        {header}
        <div className="content">
          <StateMessage title="Connector settings could not load" body={error.message} onRetry={load} />
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="content">
        {!connectors ? (
          <div className="cw-skel" style={{ width: '40%' }} />
        ) : (
          <div className="cw-conn-grid">
            {connectors.map((c) => {
              const s = statusOf(c);
              return (
                <section className="tool" key={c.id}>
                  <div className="th">
                    <span className="glyph" style={{ background: c.color }}>
                      {c.short}
                    </span>
                    <span>
                      <span className="tn">{c.name}</span>
                      <span className="tv">
                        {c.vendor} · {c.auth === 'entered' ? 'API credential entered here' : "CyberWISE's own identity"}
                      </span>
                    </span>
                    <span style={{ marginLeft: 'auto' }}>
                      <Pill kind={s.kind}>{s.label}</Pill>
                    </span>
                  </div>
                  <div className="cw-conn-b">
                    <ol className="cw-setup">
                      {c.setup.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                    {c.auth === 'entered' ? <EnteredForm c={c} onDone={load} /> : <ManagedIdentity c={c} onDone={load} />}
                  </div>
                  <div className="tf">
                    <span>
                      {c.lastTest?.at ? `Last test ${when(c.lastTest.at)}` : 'Never tested'}
                    </span>
                    <span className="cw-tf-d" title={c.lastTest?.detail ?? ''}>
                      {c.lastTest?.detail ?? ''}
                    </span>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <div className="cw-more note">
          <b>Adding a source.</b> Each connector is one adapter in the API. The ZTNA platform replacing the FortiClient VPN gets its own once the
          product is chosen — this screen picks it up with no other change.
        </div>

        <div className="sec-h mt">
          <h2>Audit log</h2>
          {audit && <span className="c">{audit.length}</span>}
        </div>
        <div className="card">
          <div className="tw">
            <table className="t cw-audit">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Connector</th>
                  <th>What happened</th>
                  <th>Who</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit && audit.length === 0 && (
                  <tr>
                    <td colSpan={5} className="cw-empty-row">
                      No connector has been configured or tested yet.
                    </td>
                  </tr>
                )}
                {audit?.map((e) => (
                  <tr key={e.id}>
                    <td className="mono">{when(e.at)}</td>
                    <td>{connectors?.find((c) => c.id === e.connectorId)?.name ?? e.connectorId}</td>
                    <td>
                      <Pill kind={e.action === 'test.passed' ? 'ok' : e.action === 'test.failed' || e.action === 'secret.retire_failed' ? 'bad' : 'info'}>
                        {ACTION_LABEL[e.action] ?? e.action}
                      </Pill>
                    </td>
                    <td>{e.actor}</td>
                    <td className="cw-audit-d">{e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
